import type { ProjectIdentity, SiteIdentity } from '../api.js';

/**
 * WHICH deal this report is about.
 *
 * The report used to open on "Investment recommendation / Advance / 68 of 100" and never name the
 * project, the address, or the sponsor anywhere on the page. On screen that was survivable — a reader
 * arrived from a project and knew. As a DOCUMENT it was not: the print stylesheet expands every
 * section precisely so a client can be sent the whole report, and what a client received was an
 * unnamed page of conclusions. Two different deals produced headers that were indistinguishable.
 *
 * Everything here is ASSERTED, never resolved — a project someone filed the run under, an address
 * typed into a form or read out of a package, a sponsor name somebody supplied. So this block states
 * identity and makes no claim about it: no parcel, no CIK, no confirmation that the address resolves.
 * The corroborated versions of these facts have their own panels in the Sources section, which is
 * where a claim that the public record AGREES belongs.
 */

/** The pieces of identity a run can carry. Each arrives by its own route, so each is independent. */
export interface DealIdentityFields {
  site?: SiteIdentity | null;
  sponsorName?: string | null;
  project?: ProjectIdentity | null;
}

const clean = (s: string | null | undefined): string | null => {
  const t = typeof s === 'string' ? s.trim() : '';
  return t.length > 0 ? t : null;
};

/** The street on its own — `line1`, plus a `line2` when the package carried one. */
function streetOf(site: SiteIdentity | null | undefined): string | null {
  const a = site?.address;
  if (!a) return null;
  const s = [clean(a.line1), clean(a.line2)].filter(Boolean).join(', ');
  return s.length > 0 ? s : null;
}

/**
 * City / state / postal on their own — "Durham, NC 27703".
 *
 * The state and postal code belong to the CITY, not to a flat comma list: joined blindly, a package
 * that states no city renders ", NC" with a leading comma, which reads as a rendering bug rather than
 * as the gap it actually is. Every asserted address is potentially partial, so each part is built
 * only from what is present.
 */
function localityOf(site: SiteIdentity | null | undefined): string | null {
  const a = site?.address;
  if (!a) return null;
  const region = [clean(a.state), clean(a.postalCode)].filter(Boolean).join(' ');
  const s = [clean(a.city), region].filter(Boolean).join(', ');
  return s.length > 0 ? s : null;
}

/** The whole address on one line. Exported for a direct test of the partial-address cases. */
export function formatAddress(site: SiteIdentity | null | undefined): string | null {
  const parts = [streetOf(site), localityOf(site)].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * The deal's display name, in the order a reader would recognize it, plus WHERE it came from.
 *
 * The project NAME is what a person calls the deal; the code is the filing key and is used only when
 * nobody supplied a name. The street falls in behind both — an address is a poor title but a far
 * better one than nothing. `source` matters to the caller: when the street became the title, the
 * location line must drop the street and show only the locality, or the header prints it twice.
 *
 * A null title is a real state, not an error: a manual form run filed under no project and given no
 * address genuinely has no name.
 */
export function dealTitle(fields: DealIdentityFields): {
  title: string | null;
  source: 'project' | 'address' | null;
} {
  const name = clean(fields.project?.name) ?? clean(fields.project?.code);
  if (name) return { title: name, source: 'project' };
  const street = streetOf(fields.site);
  if (street) return { title: street, source: 'address' };
  return { title: null, source: null };
}

/**
 * Does this run carry enough identity to be worth a header?
 *
 * A sponsor ALONE qualifies. It cannot title the report — the sponsor is who brought the deal, not
 * what the deal is, and using it as a title would quietly assert they are the same thing — but on a
 * run where it is the only fact known, naming it beats saying nothing.
 */
export function hasDealIdentity(fields: DealIdentityFields): boolean {
  return dealTitle(fields).title !== null || clean(fields.sponsorName) !== null;
}

export function DealIdentity({ fields }: { fields: DealIdentityFields }) {
  const { title, source } = dealTitle(fields);
  const sponsor = clean(fields.sponsorName);
  const code = clean(fields.project?.code);
  if (!title && !sponsor) return null;

  // When the STREET is the title, the location line carries the locality alone — otherwise the
  // header reads "6 Grace Ln" and then "6 Grace Ln · Durham, NC" directly beneath it.
  const where = source === 'address' ? localityOf(fields.site) : formatAddress(fields.site);
  // The code is a FACT only when it is not already the title, or the header reads
  // "GGP-0042 / Project GGP-0042".
  const showCode = code !== null && code !== title;

  return (
    <header className="did">
      <p className="did__eyebrow">Deal report</p>
      {/*
        The document's H1 is the DEAL. `DealHeadline` below renders the recommendation at canon §07's
        H1 SCALE (32–52px Montserrat ExtraBold) and keeps it — the scale was #367's ruled finding and
        is untouched — but drops to an h2 whenever this heading exists, because a report titled by its
        own conclusion has no title. Where no name is known this block renders no heading at all and
        the recommendation stays the h1, so the page never skips a level and never ships two.
      */}
      {title && <h1 className="did__name">{title}</h1>}
      {where && <p className="did__where">{where}</p>}
      {(showCode || sponsor) && (
        <ul className="did__facts">
          {showCode && (
            <li className="did__fact">
              <span className="did__k">Project</span>
              <span className="did__v">{code}</span>
            </li>
          )}
          {sponsor && (
            <li className="did__fact">
              {/* "As submitted" is the whole disclaimer, in two words. Nobody verified this name —
                  the filings panel in Sources makes the same point at length. */}
              <span className="did__k">Sponsor, as submitted</span>
              <span className="did__v">{sponsor}</span>
            </li>
          )}
        </ul>
      )}
    </header>
  );
}
